/**
 * Blog Post: How to Use AI Chat
 * Author: Friendly Guide (Sarah Mitchell)
 */

import type { BlogPost } from "../types";

export const howToUseChatPost: BlogPost = {
  slug: "how-to-use-ai-chat",
  title:
    "How to Use AI Chat: Getting the Best Answers to Your Building Code Questions",
  description:
    "Learn how to effectively use Builder Buddy's AI Chat to get accurate, cited answers about Calgary building codes, permits, and construction requirements.",
  excerpt:
    "Our AI Chat understands natural language questions about building codes. Here's how to get the most helpful answers.",
  author: "friendly-guide",
  category: "tools",
  featuredImage: "/blog/chat-hero.jpg",
  tags: ["AI chat", "how-to", "building codes", "tips", "getting started"],
  publishedAt: "2026-01-26",
  readingTime: 4,
  tableOfContents: [
    { id: "asking-good-questions", title: "Asking Good Questions", level: 2 },
    {
      id: "understanding-citations",
      title: "Understanding Citations",
      level: 2,
    },
    {
      id: "example-questions",
      title: "Example Questions by Category",
      level: 2,
    },
    { id: "tips-for-best-results", title: "Tips for Best Results", level: 2 },
  ],
  callToAction: {
    title: "Ready to try it?",
    description: "Start asking building code questions now.",
    buttonText: "Open AI Chat",
    buttonLink: "/chat",
  },
  content: `
Our AI Chat is the heart of Builder Buddy. It's designed to understand natural language questions about building codes and provide accurate, cited answers. Whether you're wondering about stair requirements or trying to understand fire ratings, our AI is here to help.

## Asking Good Questions

The AI works best when you ask clear, specific questions. Here are some tips:

**Be specific about what you're asking:**
- Instead of "tell me about stairs," try "what is the minimum stair width for residential buildings in Alberta?"
- Instead of "basement requirements," try "what permits do I need for basement development in Calgary?"

**Include relevant context:**
- If you're asking about a specific type of building, mention it: "for a single-family home" or "for a commercial building"
- If location matters, specify: "in Calgary" or "in Alberta"

**Ask one question at a time:**
- The AI gives better answers to focused questions
- If you have multiple questions, ask them separately

## Understanding Citations

Every answer includes citations to specific code sections. These look like:

- **NBC 9.8.4.1** - National Building Code, Section 9.8.4.1
- **NPC 2020** - National Plumbing Code 2020
- **Calgary LUB 1P2007** - Calgary Land Use Bylaw

You can look up these citations in our Code Library to see the full text of each section. Citations help you verify the information and reference it in your own documentation.

## Example Questions by Category

**Building Structure:**
- "What is the minimum stair width for residential buildings?"
- "What's the minimum ceiling height for habitable rooms?"
- "What are the handrail requirements for exterior stairs?"

**Fire & Safety:**
- "What fire separation is required between a garage and house?"
- "Where do I need smoke alarms in a house?"
- "What's the fire rating for a secondary suite?"

**Zoning & Setbacks:**
- "What is the maximum building height in R-C1 zone?"
- "What are the setback requirements for R-C2?"
- "What's the maximum lot coverage allowed?"

**Permits & Process:**
- "What permits do I need for a basement development?"
- "Do I need a permit to build a deck?"
- "What's required for a secondary suite permit?"

**Specific Requirements:**
- "What are the egress window requirements for bedrooms?"
- "What's the minimum bathroom size?"
- "How many electrical outlets are required per room?"

## Tips for Best Results

1. **Start simple, then add detail** - Begin with a straightforward question. If you need more specific information, follow up with additional details.
2. **Use the suggested questions** - We provide categorized example questions on the chat page. These are great starting points and cover the most common topics.
3. **Follow up for clarification** - If an answer isn't quite what you needed, ask a follow-up question. The AI remembers context from your conversation.
4. **Check citations in the Code Library** - For important decisions, use our Code Library to read the full code section for complete context and any exceptions.
5. **Save important answers** - For project planning, copy and save answers that are relevant to your work. The citations make it easy to reference them later.

Remember, Builder Buddy provides information for educational purposes. For official decisions, always verify with your local building authority. But we're here to help you understand the requirements and ask the right questions!
`,
};
